import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MIcon, MNote, MRow, MSection, MStats } from '@/components/mobile/MobileKit';
import { MyMarathons } from './learning/MyMarathons';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


import { usersApi } from '@/api/users';
import { progressApi } from '@/api/progress';
import { vocabApi } from '@/api/vocab';
import { computeHasFeature, useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Mascot } from '@/components/ui/Mascot';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { curatorGradesByLesson } from '@/lib/lessonFeedback';
import { LessonFeedbackPanel, LessonGradeChip } from '@/components/lesson/LessonGradeChip';

/**
 * Оқу бөлімі — the learning hub (#2).
 *
 * Redesigned from a flat marathon accordion into a study dashboard: the current
 * course (with progress + Continue), a word-progress widget, a homework
 * preview, and the Сөздік / AI-trainers shortcut cards. Tapping a course opens
 * its module tree at {@link StudentMarathonPage} (#3), which auto-expands where
 * the student left off. Discovery stays enrollment-based (private marathons
 * included) via GET /student/enrollments/me.
 */


export function StudentLessonsListPage({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const hasVocab = computeHasFeature(user, 'vocab');

  const marathons = useQuery({
    queryKey: ['student', 'enrolled-marathons'],
    queryFn: () => usersApi.myEnrolledMarathons(),
  });
  const list = marathons.data ?? [];

  // ⚠️ Основной курс берётся ПО ПРИЗНАКУ, а не по месту в списке.
  //
  // До 28.08.2026 здесь стояло `list[0]` — просто первый в том порядке, в каком
  // его вернул сервер. Пока у всех был один курс, это работало случайно. С
  // мини-курсами в тарифе курс по психологии мог бы встать на место IELTS,
  // молча и без единой ошибки.
  //
  // Запасной вариант всё же нужен: у только что зарегистрировавшегося есть
  // лишь бесплатный курс, записи на который нет, и основного среди них не
  // будет. Без запасного экран показал бы пустоту вместо курса.
  const current = list.find((m) => m.is_primary) ?? list[0];

  const progress = useQuery({
    queryKey: ['student', 'marathon-progress', current?.id],
    queryFn: () => progressApi.getMyMarathonProgress(current!.id),
    enabled: !!current,
  });

  // Оценки куратора по урокам основного курса (D11, 10.09.2026). Старый сервер
  // уроков в ответе не присылает — карта пустая, чипов нет, экран как раньше.
  const grades = useMemo(() => curatorGradesByLesson(progress.data), [progress.data]);
  // Из уведомления «куратор оценил урок» приходят с `?feedback=<урок>` — этот
  // урок раскрыт сразу.
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(() => searchParams.get('feedback'));
  const toggleFeedback = (id: string) => setExpanded((cur) => (cur === id ? null : id));

  const stats = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });

  const vocab = useQuery({
    queryKey: ['vocab', 'stats'],
    queryFn: vocabApi.getStats,
    enabled: hasVocab,
  });

  const homework = useQuery({
    queryKey: ['student', 'homework', 'preview'],
    queryFn: () => progressApi.listMyHomework(undefined, 0, 3),
  });
  const hwItems = homework.data?.items ?? [];

  const streak = stats.data?.current_streak ?? 0;

  const learnedPct = useMemo(() => {
    const s = vocab.data;
    if (!s || !s.total) return 0;
    return Math.round((s.learned / s.total) * 100);
  }, [vocab.data]);

  if (marathons.isLoading) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card variant="subtle">{t('common.loading')}</Card>
      </div>
    );
  }

  if (marathons.error) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card variant="subtle" className="text-error">{humanizeError(marathons.error)}</Card>
      </div>
    );
  }

  // ── Телефон: раскладка из макета 23.08.2026 ──────────────────────────────
  // Оранжевый блок «продолжить», числа по словам одной строкой, уроки одним
  // тонированным блоком, материалы синей строкой в конце.
  if (isMobile) {
    const v = vocab.data;
    return (
      <div className="space-y-3.5">
        {/*
          «Мои марафоны» (D23, 11.09.2026): все курсы карточками — основной и
          мини-курсы, с процентом и «N из M». Заменили оранжевый блок основного
          курса и строки «Другие курсы».

          ⚠️ Мини-курсы на телефоне по-прежнему стоят СРАЗУ сверху: до 28.08.2026
          мобильная ветка их не показывала вовсе, а курс, потерянный под словарём
          и домашкой, всё равно что отсутствует.
        */}
        {current ? (
          <MyMarathons list={list} />
        ) : (
          <MNote icon="school" title={t('lessons.noCourses')} meta={hasVocab ? t('lessons.noCoursesHint') : undefined} />
        )}

        {hasVocab && v && (
          <MStats
            items={[
              { value: v.total ?? 0, label: t('vocab.total') },
              { value: v.learned ?? 0, label: t('vocab.learned'), tone: 'info' },
              { value: Math.max((v.total ?? 0) - (v.learned ?? 0), 0), label: t('vocab.learning') },
              { value: v.due_today ?? 0, label: t('vocab.due'), tone: 'warm' },
            ]}
          />
        )}

        {current?.modules?.length ? (
          <>
            <MSection title={t('lessons.title')} right={`${learnedPct ? '' : ''}${streak ? t('dashboard.streakDays', { count: streak }) : ''}`} />
            <MBlock>
              {(current.modules ?? []).flatMap((m) => m.lessons ?? []).slice(0, 8).map((lesson, i, arr) => (
                <MRow
                  key={lesson.id}
                  leading={
                    lesson.access_status === 'passed' ? (
                      <MIcon icon="check" tone="done" />
                    ) : lesson.access_status === 'locked' ? (
                      // ⚠️ Номер урока, а не замок (решение владельца 14.09.2026): порядок
                      // прохождения прежний, строка не нажимается, но замка нет нигде.
                      <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-surface-container-high text-[12px] font-bold text-on-surface-variant">
                        {i + 1}
                      </span>
                    ) : (
                      <MIcon icon="play_arrow" tone="warm" />
                    )
                  }
                  title={lesson.title}
                  meta={
                    lesson.access_status === 'passed'
                      ? t('lessons.completed')
                      : lesson.access_status === 'locked'
                        ? t('lessons.upNext')
                        : t('lessons.current')
                  }
                  metaAccent={lesson.access_status === 'available'}
                  dim={lesson.access_status === 'locked'}
                  to={lesson.access_status === 'locked' ? undefined : `/app/lessons/${lesson.id}`}
                  last={i === arr.slice(0, 8).length - 1}
                  aside={
                    grades.has(lesson.id) ? (
                      <LessonGradeChip
                        curatorGrade={grades.get(lesson.id)}
                        expanded={expanded === lesson.id}
                        onToggle={() => toggleFeedback(lesson.id)}
                      />
                    ) : undefined
                  }
                  below={
                    expanded === lesson.id && grades.has(lesson.id) ? (
                      <LessonFeedbackPanel curatorGrade={grades.get(lesson.id)} className="mb-3" />
                    ) : undefined
                  }
                />
              ))}
            </MBlock>
          </>
        ) : null}

        {/* Материалы — виджет, а не отдельный экран: страницы `/app/materials`
            в приложении нет, и ссылка на неё вела в 404 (поймано заказчиком
            23.08.2026). Показываем их прямо здесь. */}
        {/* Синяя row «Материалдар» из макета. Раньше здесь стояла лента
            материалов целиком — потому что страницы не существовало и ссылка
            вела в 404. Теперь страница есть. */}
        <MNote icon="video_library" title={t('materials.title')} meta={t('materials.subtitle')} to="/app/materials" />

        {hwItems.length > 0 && (
          <MNote
            icon="assignment"
            title={t('learningHub.tabHomework')}
            meta={t('lessons.homeworkPending', { count: hwItems.length })}
            to="/app/lessons?tab=homework"
          />
        )}
      </div>
    );
  }

  // Плоский список уроков основного курса — как в макете: номер, название,
  // состояние. Модули в этом списке не заголовки, а порядок.
  const courseLessons = (current?.modules ?? []).flatMap((mod) => mod.lessons ?? []);
  const statusOf = (l: (typeof courseLessons)[number]) =>
    l.access_status ?? (l.is_available ? 'available' : 'locked');
  // Текущий — первый непройденный и незакрытый.
  const currentLesson = courseLessons.find((l) => statusOf(l) === 'available');

  return (
    <div>
      {!embedded && (
        <StudentTopBar
          title={t('learn.title')}
          subtitle={t('learn.subtitle')}
          streak={streak}
          name={user?.full_name}
          avatarUrl={user?.avatar_url}
        />
      )}

      {!current ? (
        <Card variant="subtle" className="flex flex-col items-center gap-4 py-10 text-center">
          <Mascot pose="books" className="mascot h-40 w-auto object-contain" />
          <p className="max-w-sm font-display text-lg font-bold tracking-display text-on-surface">
            {t('lessons.noMarathons')}
          </p>
          {/* В приложении покупки нет, поэтому кнопки «выбрать тариф» тоже нет —
              но оставлять человека на пустом экране без объяснения нельзя. Это и
              ученику обидно, и ревьюер магазина читает пустой основной раздел как
              «приложение не работает» (правило Apple 2.1). Объясняем, кто
              открывает курс, и ведём в то, что доступно бесплатно.
              ⚠️ ОДИНАКОВО И НА САЙТЕ, И В ПРИЛОЖЕНИИ (01.09.2026): отказ 30.08
              пришёл со снимком САЙТА — проверяющий ходит и туда тем же
              демо-доступом. */}
          {hasVocab && <p className="max-w-sm text-sm text-on-surface-variant">{t('lessons.noCoursesHint')}</p>}
          {/* ⚠️ Ведём в СЛОВАРЬ, не в тренажёры (02.10.2026): человек без курса — бесплатник, раздела тренажёров
              у него нет, и кнопка приводила его на заглушку «скоро вернутся». Нет и словаря — кнопки нет. */}
          {hasVocab && (
            <Link to="/app/vocabulary">
              <Button>{t('lessons.openVocabulary')}</Button>
            </Link>
          )}
        </Card>
      ) : (
        <>
          {/* ── Мои марафоны (D23, 11.09.2026) ──────────────────────────────
              Карточки всех курсов вместо полосы прогресса основного и плиток
              «Дополнительные курсы»: процент и «N из M» теперь в карточке. */}
          <MyMarathons list={list} className="mb-[18px]" />

          {/* ── Слова этого курса ───────────────────────────────────────── */}
          {hasVocab && (
            <div className="mb-[18px] rounded-[20px] bg-surface-container px-5 py-[15px]">
              <div className="mb-[11px] flex items-center gap-3">
                <h3 className="flex-1 text-[13px] font-semibold text-on-surface">{t('learn.wordProgress')}</h3>
                <Link to="/app/vocabulary" className="text-[12.5px] font-semibold text-info hover:underline">
                  {t('learn.openVocab')}
                </Link>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: vocab.data?.total ?? 0, l: t('learn.wordsTotal'), c: 'text-on-surface' },
                  { v: vocab.data?.learned ?? 0, l: t('learn.wordsMastered'), c: 'text-info' },
                  { v: Math.max((vocab.data?.total ?? 0) - (vocab.data?.learned ?? 0), 0), l: t('learn.wordsLearning'), c: 'text-on-surface' },
                  { v: vocab.data?.due_today ?? 0, l: t('learn.wordsDue'), c: 'text-on-primary-container' },
                ].map((x) => (
                  <div key={x.l}>
                    <div className={cn('font-display text-xl font-bold tabular-nums', x.c)}>{x.v}</div>
                    <div className="text-[11px] text-on-surface-variant">{x.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Уроки ───────────────────────────────────────────────────── */}
          <h3 className="mx-0.5 mb-2.5 font-display text-[15.5px] font-bold text-on-surface">
            {t('marathon.modules')}
          </h3>
          {courseLessons.length === 0 ? (
            <p className="rounded-[20px] bg-surface-container px-[18px] py-4 text-[13px] text-on-surface-variant">
              {t('lessons.noLessons')}
            </p>
          ) : (
            <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
              {courseLessons.map((l, i) => {
                // ⚠️ СОСТОЯНИЙ ЧЕТЫРЕ, а не три. Сперва я считал «не пройден и
                // не текущий» закрытым — и открытые уроки подписывались
                // «Закрыт», предлагая при этом «Повторить». Открытый урок,
                // до которого человек ещё не дошёл, — обычное дело.
                const st = statusOf(l);
                const passed = st === 'passed';
                const locked = st === 'locked';
                const inProgress = st === 'available' && l.id === currentLesson?.id;
                const open = st === 'available' && !inProgress;
                const grade = grades.get(l.id);
                const feedbackOpen = !!grade && expanded === l.id;
                const lead = (
                  <>
                    <span
                      className={cn(
                        'grid flex-none place-items-center rounded-full text-[12px] font-bold',
                        inProgress ? 'h-[30px] w-[30px] bg-primary text-on-primary' : 'h-[26px] w-[26px]',
                        passed && 'bg-info text-white',
                        locked && 'bg-surface-container-high text-on-surface-variant',
                      )}
                    >
                      {passed ? (
                        <span className="material-symbols-outlined text-[14px]">check</span>
                      ) : inProgress ? (
                        <span className="material-symbols-outlined text-[13px]">play_arrow</span>
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-[13.5px] text-on-surface', inProgress ? 'font-bold' : 'font-semibold')}>
                        {l.title}
                      </span>
                      <span
                        className={cn(
                          'block text-[12px]',
                          inProgress ? 'text-on-primary-container' : 'text-on-surface-variant',
                        )}
                      >
                        {passed
                          ? t('lessons.done')
                          : inProgress
                            ? t('lessons.inProgress')
                            : open
                              ? t('lessons.open')
                              : t('lessons.upNext')}
                      </span>
                    </span>
                  </>
                );
                const tail = inProgress ? (
                  <span className="flex-none rounded-full bg-primary px-4 py-[7px] text-[11px] font-bold text-on-primary">
                    {t('lessons.start')}
                  </span>
                ) : locked ? null : (
                  <span className="flex-none text-[12.5px] font-semibold text-on-surface-variant">
                    {passed ? t('lessons.review') : t('lessons.openLesson')}
                  </span>
                );

                // Закрытый урок не ссылка: переход в замок — это тупик, из
                // которого человек возвращается кнопкой «назад».
                if (locked) {
                  return (
                    <div key={l.id} className="flex items-center gap-3 border-b border-outline py-3 opacity-50 last:border-b-0">
                      {lead}
                      {tail}
                    </div>
                  );
                }

                // ⚠️ ССЫЛКА — НЕ ВСЯ СТРОКА (D11, 10.09.2026). До фидбека куратора
                // вся строка была одной ссылкой. Чип «90/100» и стрелка — кнопки
                // между названием и «Повторить»; внутри ссылки нажатие на них
                // уводило бы в урок вместо раскрытия. Поэтому ссылок две — на
                // название и на «Повторить», вторая скрыта от читалки экрана.
                return (
                  <div
                    key={l.id}
                    className={cn(
                      'border-b border-outline last:border-b-0',
                      inProgress && '-mx-[18px] rounded-[18px] border-b-0 bg-primary-container px-[18px]',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/app/lessons/${l.id}`}
                        className={cn('flex min-w-0 flex-1 items-center gap-3', inProgress ? 'py-3.5' : 'py-3')}
                      >
                        {lead}
                      </Link>
                      <LessonGradeChip
                        curatorGrade={grade}
                        expanded={feedbackOpen}
                        onToggle={() => toggleFeedback(l.id)}
                      />
                      <Link to={`/app/lessons/${l.id}`} tabIndex={-1} aria-hidden="true" className="flex-none">
                        {tail}
                      </Link>
                    </div>
                    {feedbackOpen && <LessonFeedbackPanel curatorGrade={grade} className="mb-3" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* «Дополнительные курсы» плитками убраны 11.09.2026 (D23): все курсы —
              карточками «Мои марафоны» сверху вкладки. */}
        </>
      )}
    </div>
  );
}
